import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService, AnalyticsEntry } from '../../services/analytics.service';
import { Attraction, ATTRACTIONS_PAGE_SIZE } from '../../models/attraction.model';
import { AttractionsService } from '../../services/attractions.service';
import { AttractionDetailsSheetService } from '../../services/attraction-details-sheet.service';
import { NotificationDeepLinkService } from '../../services/notification-deep-link.service';
import { heroImageUrl } from '../attraction-utils';

/**
 * Attractions for the current venue's city (Android `TouristAttractionsScreen`).
 *
 * Structure follows the GetYourGuide app: hero → "Attractions you can't miss" carousel → the full
 * paginated list. The venue is resolved from `sessionStorage.clientData.id` (no route param — the
 * backend knows the city); `city` here is only the hero title. Colours come from the fixed
 * `attractions-scope` palette, never the venue theme.
 */
@Component({
    selector: 'app-attractions-list',
    templateUrl: './attractions-list.component.html',
    styleUrls: ['./attractions-list.component.scss'],
    host: { class: 'attractions-scope' },
    standalone: false
})
export class AttractionsListComponent implements OnInit, OnDestroy {
  city = '';
  items: Attraction[] = [];
  recommended: Attraction[] = [];
  totalCount = 0;

  loading = false;
  loadingMore = false;
  error: string | null = null;

  /** Back button follows scroll direction: shown at top / on scroll-up, hidden on scroll-down. */
  isBackVisible = true;

  /** Placeholder rows for the loading skeleton (mirrors the Android 5-row skeleton). */
  readonly skeletonRows = [1, 2, 3, 4, 5];

  private shopId: number | string | null = null;
  private lastScrollY = 0;
  /** Notification deep link (`?code=`) — see `handleNotificationDeepLink`. */
  private deepLinkSub?: Subscription;

  /** Fire load-more this many pixels before the true bottom (a few cards early, GYG-style). */
  private static readonly LOAD_MORE_THRESHOLD_PX = 600;

  constructor(
    private attractionsService: AttractionsService,
    private sheet: AttractionDetailsSheetService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private translate: TranslateService,
    private analytics: AnalyticsService,
    private deepLinks: NotificationDeepLinkService
  ) {}

  ngOnInit(): void {
    const raw = sessionStorage.getItem('clientData');
    if (!raw) {
      this.router.navigate(['']);
      return;
    }
    let clientData: any;
    try {
      clientData = JSON.parse(raw);
    } catch {
      this.router.navigate(['']);
      return;
    }

    this.shopId = clientData?.id ?? null;
    this.city = clientData?.attractions_city ?? '';

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.loadInitial();
    this.handleNotificationDeepLink();
  }

  /**
   * `?code=<provider_product_id>` — where the touristic venue's attraction notification lands.
   *
   * The list opens underneath and the details sheet opens on top of it, which is the same shape as
   * tapping a card: browser Back closes the sheet onto the Explore list rather than leaving the app.
   * Nothing waits for `loadInitial()` — the sheet fetches its own attraction by code, so it can paint
   * while the catalogue behind it is still loading, and it opens for a code that is not on the first
   * page (or in the catalogue at all — it renders its own not-found state).
   *
   * A subscription rather than a snapshot read, for the warm path: a tap while the app already sits
   * on `/attractions` changes only the query string, and Angular re-uses the component instead of
   * running `ngOnInit` again.
   */
  private handleNotificationDeepLink(): void {
    // Unsubscribed on destroy: this one acts on the router and the global sheet, so an instance left
    // listening after the guest has navigated away would open a sheet over someone else's screen.
    this.deepLinkSub = this.route.queryParamMap.subscribe(params => {
      const code = (params.get('code') || '').trim();
      if (!code) return;
      // Once per page load, however the history stack ends up looking — see the service.
      if (!this.deepLinks.consume(`attractions:code=${code}`)) return;
      // Deferred one tick: this fires inside the navigation that is still activating this component.
      setTimeout(() => this.openFromNotification(code), 0);
    });
  }

  private openFromNotification(code: string): void {
    // `Location`, not a router navigation: the latter emits NavigationStart, and
    // `AttractionDetailsSheetService` closes itself on that — so the sheet this is about to open
    // would shut again a tick later. `NotificationDeepLinkService` is what keeps this to one firing;
    // the strip is only here so a reload does not replay it.
    this.location.replaceState('/attractions');
    this.open(code, AnalyticsEntry.DEEPLINK);
  }

  ngOnDestroy(): void {
    this.deepLinkSub?.unsubscribe();
    window.removeEventListener('scroll', this.onScroll);
  }

  /**
   * The attraction the hero IS: the venue's highlighted pick leads, so this matches the home banner.
   * With nothing highlighted it falls back to the first pick, then to the first attraction the server
   * sent — which is the best-rated only because the list arrives in rating order, not because
   * anything here sorts.
   *
   * Held in a field rather than recomputed by each getter so the photo, the name in the overlay and
   * the tap target are provably the same attraction and can never drift apart — and so the `.find()`
   * does not re-run on every change-detection pass.
   */
  heroAttraction: Attraction | null = null;

  get heroImageUrl(): string | null {
    return heroImageUrl(this.heroAttraction);
  }

  get heroTitle(): string {
    return this.heroAttraction?.title ?? '';
  }

  /**
   * The code the hero opens, or null when nothing resolves — which also covers an attraction the
   * provider gave no id for, since the details sheet is keyed on that id and cannot open without it.
   * Null leaves the hero inert rather than a tap that does nothing.
   */
  get heroCode(): string | null {
    return this.heroAttraction?.provider_product_id ?? null;
  }

  onHeroClick(): void {
    const code = this.heroCode;
    if (code) {
      this.open(code, AnalyticsEntry.HERO);
    }
  }

  /** Re-resolve after both the list and the recommended strip have landed. */
  private resolveHeroAttraction(): void {
    const highlighted = this.recommended.find(r => r.is_highlighted === true);
    this.heroAttraction = highlighted ?? this.recommended[0] ?? this.items[0] ?? null;
  }

  get canLoadMore(): boolean {
    return this.items.length < this.totalCount;
  }

  private async loadInitial(): Promise<void> {
    if (this.shopId === null) {
      this.error = this.translate.instant('attractions.error');
      return;
    }

    this.loading = true;
    this.error = null;

    // Both in parallel — independent, and the strip usually paints first. Mirrors the Android VM.
    const [list, recommended] = await Promise.all([
      this.attractionsService.getAttractions(this.shopId, 1),
      this.attractionsService.getRecommended(this.shopId)
    ]);

    // A failed catalogue is the screen's error; a missing strip must not fail the screen.
    if (!list) {
      this.error = this.translate.instant('attractions.error');
    } else {
      this.items = list.items ?? [];
      this.totalCount = list.total_count ?? this.items.length;
    }
    if (recommended) {
      this.recommended = recommended.items ?? [];
    }

    // After BOTH assignments: the two responses land together but are assigned in separate branches,
    // and the hero's resolution order spans them.
    this.resolveHeroAttraction();

    this.loading = false;
  }

  private async loadNextPage(): Promise<void> {
    if (this.shopId === null || this.loading || this.loadingMore || !this.canLoadMore) return;

    this.loadingMore = true;
    // Derive the page from what we hold rather than tracking an index: a failed page simply retries.
    const page = Math.floor(this.items.length / ATTRACTIONS_PAGE_SIZE) + 1;
    const result = await this.attractionsService.getAttractions(this.shopId, page);

    if (result) {
      const more = result.items ?? [];
      if (more.length) {
        // Guard a backend that ignores `page` and replays page 1: dedupe by provider_product_id.
        const seen = new Set(this.items.map(i => i.provider_product_id).filter(Boolean));
        this.items = this.items.concat(more.filter(m => !seen.has(m.provider_product_id)));
      }
      if (result.total_count != null) {
        this.totalCount = result.total_count;
      }
      // Success only: a page that failed to load was not browsed.
      this.analytics.log('attractions_list_paged', { list_page: page });
    }
    this.loadingMore = false;
  }

  private onScroll = (): void => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // Back-button visibility from scroll direction.
    if (scrollTop <= 0) {
      this.isBackVisible = true;
    } else if (scrollTop > this.lastScrollY) {
      this.isBackVisible = false;
    } else if (scrollTop < this.lastScrollY) {
      this.isBackVisible = true;
    }
    this.lastScrollY = scrollTop;

    // Load-more when near the bottom.
    const clientHeight = document.documentElement.clientHeight;
    const scrollHeight = document.documentElement.scrollHeight;
    if (scrollHeight - (scrollTop + clientHeight) < AttractionsListComponent.LOAD_MORE_THRESHOLD_PX) {
      this.loadNextPage();
    }
  };

  /**
   * `entryPoint` distinguishes the three ways in on this screen — the hero, the curated strip, and
   * the plain catalogue. Knowing whether curation actually outperforms the raw list is the whole
   * reason the venue admin gets to pick, so they cannot share one anonymous handler.
   */
  onOpen(code: string, entryPoint: string = AnalyticsEntry.LIST): void {
    this.open(code, entryPoint);
  }

  private open(code: string, entryPoint: string): void {
    this.analytics.log('attraction_open', { content_id: code, entry_point: entryPoint });
    this.sheet.open(code);
  }

  back(): void {
    this.location.back();
  }

  retry(): void {
    this.loadInitial();
  }

  trackByCode(_index: number, attraction: Attraction): string {
    return attraction.provider_product_id ?? String(_index);
  }
}
